import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type MutableRefObject } from 'react';

import { apiService } from '../../services';
import type { PersonFormBaseValues, PersonFormSection, PersonFormSectionControl } from '../../types/vida/cadastralMaintenance/personFormTypes';
import { formatCepFieldValue, getCepDigits, parseCepParts } from '../../utils/cepFieldUtils';
import {
  getInputValue,
  limitUfInput,
  resolvePersonFormFieldUpdate,
} from '../../utils/insuredFormUtils';
import { getNextPersonFormSection } from '../../utils/personFormSectionUtils';
import {
  isZipCodeComplete,
  mapZipCodeAddressItemToFormFields,
} from '../../utils/zipCodeAddressUtils';

type UsePersonFormSectionParams<T extends PersonFormBaseValues> = {
  initialValues?: T;
  getFormValuesFromInitial: (initialValues?: T) => T;
  isBaseFormValid: (values: T) => boolean;
  readOnly?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  onValuesChange?: (values: T) => void;
  sectionControlRef?: MutableRefObject<PersonFormSectionControl | null>;
  onActiveSectionChange?: (section: PersonFormSection) => void;
};

export function usePersonFormSection<T extends PersonFormBaseValues>({
  initialValues,
  getFormValuesFromInitial,
  isBaseFormValid,
  readOnly = false,
  onValidityChange,
  onValuesChange,
  sectionControlRef,
  onActiveSectionChange,
}: UsePersonFormSectionParams<T>) {
  const lockedZipCodeRef = useRef<string | null>(null);
  const prevReadOnlyRef = useRef(readOnly);
  const [values, setValues] = useState<T>(() => getFormValuesFromInitial(initialValues));
  const [activeSection, setActiveSection] = useState<PersonFormSection>('personData');
  const [isCpfValid, setIsCpfValid] = useState(false);
  const [isZipCodeLookupLoading, setIsZipCodeLookupLoading] = useState(false);
  const [zipCodeLookupError, setZipCodeLookupError] = useState<string | null>(null);
  const [isAddressLockedByZipCodeLookup, setIsAddressLockedByZipCodeLookup] = useState(false);

  useEffect(() => {
    setValues(getFormValuesFromInitial(initialValues));
    setActiveSection('personData');
    setZipCodeLookupError(null);
    lockedZipCodeRef.current = null;
    setIsAddressLockedByZipCodeLookup(false);
  }, [initialValues]);

  useEffect(() => {
    if (prevReadOnlyRef.current && !readOnly) {
      setActiveSection('personData');
    }
    prevReadOnlyRef.current = readOnly;
  }, [readOnly]);

  const clearLockedAddressFields = useCallback(() => {
    lockedZipCodeRef.current = null;
    setIsAddressLockedByZipCodeLookup(false);
    setValues((prev) => ({
      ...prev,
      logradouro: '',
      bairro: '',
      cidade: '',
      uf: '',
      ...('numero' in prev ? { numero: '' } : {}),
    }));
  }, []);

  const handleInputChange =
    (field: keyof T) => (e: FormEvent<HTMLBsdsInputElement>) => {
      const value = getInputValue(e);
      const { partial } = resolvePersonFormFieldUpdate(field, value, values);

      if (field === 'cep') {
        setZipCodeLookupError(null);

        const nextCep = partial.cep ?? values.cep;
        const nextCepDigits = getCepDigits(nextCep);
        const shouldClearLockedAddress =
          lockedZipCodeRef.current != null
          && (nextCepDigits !== lockedZipCodeRef.current
            || !isZipCodeComplete(nextCep));

        if (shouldClearLockedAddress) {
          lockedZipCodeRef.current = null;
          setIsAddressLockedByZipCodeLookup(false);
          setValues((prev) => ({
            ...prev,
            ...partial,
            logradouro: '',
            cidade: '',
            uf: '',
            ...('numero' in prev ? { numero: '' } : {}),
          }));
        } else {
          setValues((prev) => ({
            ...prev,
            ...partial,
          }));
        }
        return;
      }

      setValues((prev) => ({
        ...prev,
        ...partial,
      }));
    };

  const handleCepChange = useCallback((cep: string) => {
    if (readOnly) {
      return;
    }

    const formattedCep = formatCepFieldValue(cep);
    setZipCodeLookupError(null);

    const nextCepDigits = getCepDigits(formattedCep);
    const shouldClearLockedAddress =
      lockedZipCodeRef.current != null
      && (nextCepDigits !== lockedZipCodeRef.current
        || !isZipCodeComplete(formattedCep));

    if (shouldClearLockedAddress) {
      lockedZipCodeRef.current = null;
      setIsAddressLockedByZipCodeLookup(false);
      setValues((prev) => ({
        ...prev,
        cep: formattedCep,
        logradouro: '',
        cidade: '',
        uf: '',
        ...('numero' in prev ? { numero: '' } : {}),
      }));
      return;
    }

    setValues((prev) => ({
      ...prev,
      cep: formattedCep,
    }));
  }, [readOnly]);

  const handleZipCodeSearch = useCallback(async () => {
    if (readOnly) {
      return;
    }

    const { cep } = values;

    if (!isZipCodeComplete(cep)) {
      setZipCodeLookupError('Informe um CEP válido');
      if (lockedZipCodeRef.current != null) {
        clearLockedAddressFields();
      }
      return;
    }

    const { cepPrefixo, cepSufixo } = parseCepParts(cep);

    setIsZipCodeLookupLoading(true);
    setZipCodeLookupError(null);

    try {
      const response = await apiService.cep.getStreetAddressByZipCode({
        numPrefixoCEP: Number(cepPrefixo),
        numSufixoCEP: Number(cepSufixo),
        ...(values.logradouro.trim()
          ? { descrLogradouro: values.logradouro.trim() }
          : {}),
      });

      const firstMatch = response.retorno?.[0];
      if (!firstMatch) {
        setZipCodeLookupError('CEP não encontrado');
        clearLockedAddressFields();
        return;
      }

      const addressFields = mapZipCodeAddressItemToFormFields(firstMatch);
      lockedZipCodeRef.current = getCepDigits(cep);
      setIsAddressLockedByZipCodeLookup(true);
      setValues((prev) => ({
        ...prev,
        ...addressFields,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao consultar CEP';
      setZipCodeLookupError(message);
      clearLockedAddressFields();
    } finally {
      setIsZipCodeLookupLoading(false);
    }
  }, [clearLockedAddressFields, readOnly, values]);

  const handleCpfChange = (cpf: string) => {
    setValues((prev) => ({
      ...prev,
      cpf,
    }));
  };

  const handleDropdownChange = (field: keyof T) => (value: string) => {
    const nextValue = field === 'uf' ? limitUfInput(value) : value;
    setValues((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  const goToNextSection = useCallback(() => {
    setActiveSection((current) => {
      const next = getNextPersonFormSection(current);
      return next ?? current;
    });
  }, []);

  const isFormValid = useMemo(
    () => isBaseFormValid(values) && isCpfValid,
    [isBaseFormValid, isCpfValid, values],
  );

  useEffect(() => {
    onValidityChange?.(isFormValid);
  }, [isFormValid, onValidityChange]);

  useEffect(() => {
    onValuesChange?.(values);
  }, [onValuesChange, values]);

  useEffect(() => {
    onActiveSectionChange?.(activeSection);
  }, [activeSection, onActiveSectionChange]);

  useEffect(() => {
    if (!sectionControlRef) {
      return;
    }

    sectionControlRef.current = {
      setActiveSection,
      goToNextSection,
    };

    return () => {
      sectionControlRef.current = null;
    };
  }, [goToNextSection, sectionControlRef]);

  return {
    values,
    activeSection,
    setActiveSection,
    goToNextSection,
    isCpfValid,
    setIsCpfValid,
    handleInputChange,
    handleCepChange,
    handleCpfChange,
    handleDropdownChange,
    handleZipCodeSearch,
    isZipCodeLookupLoading,
    zipCodeLookupError,
    isAddressLockedByZipCodeLookup,
    inputDisabled: readOnly,
  };
}
